import Dexie from 'dexie';
import moment from 'moment';

const originalConsoleLog = console.log;
console.logStyled = function (...args) {
  if (typeof args[0] === 'string') {
    originalConsoleLog.call(
      console,
      '%c' + args[0],
      'color: #faad41; font-weight: bold;',
      ...args.slice(1)
    );
  } else {
    originalConsoleLog.apply(console, args);
  }
};

/**
 * Створення бази даних для конкретної сторінки.
 */
function createDBForPage(pageId) {
  const dbName = `DynamicTableDB_${pageId}`;
  const db = new Dexie(dbName);
  // Додаємо поле customOrder для збереження порядку сортування
  db.version(1).stores({
    items:
      '++key, group, parentId, id, customOrder, name, is_active, ' +
      'qualified_count, qualified_amount, qualified_avg_amount, qualified_rd_avg_amount, qualified_median_amount, qualified_cr_deposit_2, qualified_cr_deposit_3, qualified_cr_deposit_4, qualified_cr_deposit_5, ' +
      'deposit_1_count, deposit_1_amount, deposit_1_avg_amount, deposit_1_median_amount, deposit_1_cr_deposit_2, deposit_1_cr_deposit_3, deposit_1_cr_deposit_4, deposit_1_cr_deposit_5, ' +
      'deposit_2_count, deposit_2_amount, deposit_2_avg_amount, deposit_2_median_amount, deposit_2_avg_time, deposit_2_median_time, deposit_2_cr_deposit_3, deposit_2_cr_deposit_4, deposit_2_cr_deposit_5, ' +
      'deposit_3_count, deposit_3_amount, deposit_3_avg_amount, deposit_3_median_amount, deposit_3_avg_time, deposit_3_median_time, deposit_3_cr_deposit_4, deposit_3_cr_deposit_5, ' +
      'deposit_4_count, deposit_4_amount, deposit_4_avg_amount, deposit_4_median_amount, deposit_4_avg_time, deposit_4_median_time, deposit_4_cr_deposit_5, ' +
      'deposit_5_count, deposit_5_amount, deposit_5_avg_amount, deposit_5_median_amount, deposit_5_avg_time, deposit_5_median_time, ' +
      'deposits_count, deposits_amount, deposits_avg_amount, deposits_median_amount, ' +
      'repeats_count, repeats_amount, repeats_avg_amount, repeats_median_amount, ' +
      'players_count, players_sum_amount, players_avg_amount, players_median_amount',
  });
  return db;
}

/**
 * Розформування айтема з бекенду.
 * Додається group, parentId, isOpen, isExpandable, isSource та customOrder.
 */
function flattenItem(item, groupName, parentId, groups, order = 0) {
  const findIndexGroup = groups.findIndex((elem) => elem === groupName);
  return {
    group: groupName,
    compositeId: `${parentId}-${groupName}_${item.id}`,
    id: item.id,
    name: item.name,
    avatar: item.avatar,
    is_active: item.is_active,
    parentId,
    ...item.qualified,
    ...item.deposit_1,
    ...item.deposit_2,
    ...item.deposit_3,
    ...item.deposit_4,
    ...item.deposit_5,
    ...item.deposits,
    ...item.repeats,
    ...item.players,
    isOpen: false,
    isExpandable: findIndexGroup < groups.length - 1 ? true : false,
    isSource: null,
    customOrder: order, // початковий порядок
  };
}

/**
 * Додавання айтемів у базу.
 * Масив items спочатку сортується згідно з параметрами sortOptions
 * (за замовчуванням { sortField: 'id', sortOrder: 'desc' }), після чого кожному айтему
 * присвоюється customOrder згідно з позицією у відсортованому масиві.
 */
const getOrdinal = (id) => {
  const ordinals = {
    1: 'Первый',
    2: 'Второй',
    3: 'Третий',
    4: 'Четвёртый',
    5: 'Пятый',
    6: 'Шестой',
    7: 'Седьмой',
    8: 'Восьмой',
    9: 'Девятый',
    10: 'Десятый',
    11: 'Одиннадцатый и больше',
  };

  return ordinals[id] || ``;
};

async function addFlattenedItemsToGroup(
  db,
  items,
  groupName,
  parent = null,
  groups,
  sortOptions = {sortField: 'id', sortOrder: 'asc'}
) {
  // Якщо група є day, month або year - перетворюємо id на мілісекунди та сортуємо
  if (['day', 'month', 'year'].includes(groupName)) {
    items = items
      .map((item) => {
        const timestamp =
          groupName === 'month'
            ? moment(item.id, 'MM.YYYY').valueOf()
            : moment(item.id, 'DD.MM.YYYY').valueOf(); // ← важливо!
        delete item.is_active;
        return {
          ...item,
          id: timestamp,
        };
      })
      .sort((a, b) => {
        if (Number(a[sortOptions.sortField]) < Number(b[sortOptions.sortField]))
          return sortOptions.sortOrder === 'desc' ? 1 : -1;
        if (Number(a[sortOptions.sortField]) > Number(b[sortOptions.sortField]))
          return sortOptions.sortOrder === 'desc' ? -1 : 1;
        return 0;
      });
  } else if (['campaign', 'adset', 'ad'].includes(groupName)) {
    items = items
      .map((item) => ({
        ...item,
        id: /^\d+$/.test(item.id) ? Number(item.id) : item.id,
        name: /^\d+$/.test(item.id) ? item.name : 'Не известно',
      }))
      .sort((a, b) => {
        if (Number(a[sortOptions.sortField]) < Number(b[sortOptions.sortField]))
          return sortOptions.sortOrder === 'desc' ? 1 : -1;
        if (Number(a[sortOptions.sortField]) > Number(b[sortOptions.sortField]))
          return sortOptions.sortOrder === 'desc' ? -1 : 1;
        return 0;
      });
  } else if (['deposit'].includes(groupName)) {
    items = items
      .map((item) => {
        return {...item, name: getOrdinal(item?.id)};
      })
      .sort((a, b) => {
        if (Number(a[sortOptions.sortField]) < Number(b[sortOptions.sortField]))
          return sortOptions.sortOrder === 'desc' ? 1 : -1;
        if (Number(a[sortOptions.sortField]) > Number(b[sortOptions.sortField]))
          return sortOptions.sortOrder === 'desc' ? -1 : 1;
        return 0;
      });
  } else if (['app_type', 'traffic_type', 'traffic_source'].includes(groupName)) {
    items = items.sort((a, b) => {
      if (Number(a[sortOptions.sortField]) < Number(b[sortOptions.sortField]))
        return sortOptions.sortOrder === 'desc' ? 1 : -1;
      if (Number(a[sortOptions.sortField]) > Number(b[sortOptions.sortField]))
        return sortOptions.sortOrder === 'desc' ? -1 : 1;
      return 0;
    });
  } else {
    // Стандартне сортування для інших груп
    items = items
      ?.map((item) => {
        const isOnlyDigits =
          typeof item.id === 'number'
            ? item.id !== 0
            : typeof item.id === 'string' && /^\d+$/.test(item.id) && item.id !== '0';

        const baseName = 'name' in item ? item.name : `${item.first_name} ${item.last_name}`;

        return {...item, name: isOnlyDigits ? `#${item.id} ${baseName}` : 'Не известно'};
      })
      .sort((a, b) => {
        if (Number(a[sortOptions.sortField]) < Number(b[sortOptions.sortField]))
          return sortOptions.sortOrder === 'desc' ? 1 : -1;
        if (Number(a[sortOptions.sortField]) > Number(b[sortOptions.sortField]))
          return sortOptions.sortOrder === 'desc' ? -1 : 1;
        return 0;
      });
  }

  const flattenedItems = items.map((item, index) =>
    flattenItem(item, groupName, parent, groups, index)
  );
  try {
    await db.items.bulkAdd(flattenedItems);
    // console.logStyled(
    //   `Дані успішно додано до бази ${db.name} для групи: ${groupName}, parent: ${parent}`
    // );
    return flattenedItems;
  } catch (error) {
    console.error('Помилка при додаванні даних:', error);
    return null;
  }
}

/**
 * Очищення таблиці items для конкретної бази.
 */
async function resetItemsTable(db) {
  try {
    await db.items.clear();
    // console.logStyled(`Таблиця items у базі ${db.name} очищена`);
  } catch (error) {
    console.error('Помилка при очищенні таблиці:', error);
  }
}

/**
 * Отримання ієрархічного плоского списку із сортуванням за customOrder.
 * Використовується для відображення даних із груповим сортуванням.
 */
async function getHierarchicalFlatListSorted(db) {
  try {
    const items = await db.items.toArray();
    const lookup = {};
    items.forEach((item) => {
      lookup[item.compositeId] = {...item, children: []};
    });
    const tree = [];
    items.forEach((item) => {
      if (item.isSource) {
        tree.push(lookup[item.compositeId]);
      } else {
        if (lookup[item.parentId]) {
          lookup[item.parentId].children.push(lookup[item.compositeId]);
        } else {
          tree.push(lookup[item.compositeId]);
        }
      }
    });
    const flatten = (nodes) => {
      const sortedNodes = nodes.slice().sort((a, b) => a.customOrder - b.customOrder);
      let result = [];
      sortedNodes.forEach((node) => {
        result.push(node);
        if (node.children && node.children.length > 0) {
          result = result.concat(flatten(node.children));
        }
      });
      return result;
    };
    const sortedTree = tree.slice().sort((a, b) => a.customOrder - b.customOrder);
    return flatten(sortedTree);
  } catch (error) {
    console.error('Помилка при отриманні ієрархічного плоского списку:', error);
    throw error;
  }
}

/**
 * Отримання глобально відсортованого плоского списку.
 * Використовується, коли потрібно відсортувати всі дані за певним полем,
 * не враховуючи групування за customOrder, але збережучи ієрархію (батьки – діти).
 */
async function getHierarchicalFlatListSortedGlobal(db, sortField, sortOrder = 'desc') {
  try {
    const items = await db.items.toArray();
    const lookup = {};
    items.forEach((item) => {
      lookup[item.compositeId] = {...item, children: []};
    });
    const tree = [];
    items.forEach((item) => {
      if (item.isSource) {
        tree.push(lookup[item.compositeId]);
      } else {
        if (lookup[item.parentId]) {
          lookup[item.parentId].children.push(lookup[item.compositeId]);
        } else {
          tree.push(lookup[item.compositeId]);
        }
      }
    });
    const flatten = (nodes) => {
      const sortedNodes = nodes.slice().sort((a, b) => {
        if (a[sortField] < b[sortField]) return sortOrder === 'desc' ? 1 : -1;
        if (a[sortField] > b[sortField]) return sortOrder === 'desc' ? -1 : 1;
        return 0;
      });
      let result = [];
      sortedNodes.forEach((node) => {
        result.push(node);
        if (node.children && node.children.length > 0) {
          result = result.concat(flatten(node.children));
        }
      });
      return result;
    };
    const sortedTree = tree.slice().sort((a, b) => {
      if (a[sortField] < b[sortField]) return sortOrder === 'desc' ? 1 : -1;
      if (a[sortField] > b[sortField]) return sortOrder === 'desc' ? -1 : 1;
      return 0;
    });
    return flatten(sortedTree);
  } catch (error) {
    console.error('Помилка при отриманні глобально відсортованого списку:', error);
    throw error;
  }
}

/**
 * Сортування конкретної групи.
 * Фільтруємо всі записи, де item.group === targetGroup (без врахування parentId),
 * і оновлюємо для них customOrder згідно з заданим полем.
 */
async function sortGroupInDB(db, {targetGroup, sortField, sortOrder = 'desc'}) {
  const groupItems = await db.items.filter((item) => item.group === targetGroup).toArray();
  if (!groupItems.length) return;
  const startOrder = Math.min(...groupItems.map((item) => item.customOrder));
  groupItems.sort((a, b) => {
    if (Number(a[sortField]) < Number(b[sortField])) return sortOrder === 'desc' ? 1 : -1;
    if (Number(a[sortField]) > Number(b[sortField])) return sortOrder === 'desc' ? -1 : 1;
    return 0;
  });
  const updatedItems = groupItems.map((item, index) => ({
    ...item,
    customOrder: startOrder + index,
  }));
  await db.items.bulkPut(updatedItems);
}

/**
 * Видалення бази даних.
 */
async function deleteDatabase(db) {
  try {
    await db.delete();
    // console.logStyled(`База даних ${db.name} успішно видалена`);
  } catch (error) {
    console.error('Помилка при видаленні бази даних:', error);
  }
}

/**
 * Оновлення айтема за compositeId.
 */
async function updateItemByIdAndGroup(db, compositeId, updatedData) {
  try {
    const count = await db.items
      .toCollection()
      .filter((item) => item.compositeId === compositeId)
      .modify(updatedData);
    if (count > 0) {
      // console.logStyled(`Оновлено ${count} запис(ів) для айтема з compositeId ${compositeId}`);
    } else {
      // console.logStyled(`Айтем з compositeId ${compositeId} не знайдено`);
    }
    return count;
  } catch (error) {
    console.error('Помилка при оновленні айтема:', error);
    throw error;
  }
}
/**
 * Оновлення айтема за group.
 */
async function updateItemByGroup(db, group, updatedData) {
  try {
    const count = await db.items
      .toCollection()
      .filter((item) => item.group === group)
      .modify(updatedData);
    if (count > 0) {
      // console.logStyled(`Оновлено ${count} запис(ів) для айтема з group ${group}`);
    } else {
      // console.logStyled(`Айтем з group ${group} не знайдено`);
    }
    return count;
  } catch (error) {
    console.error('Помилка при оновленні айтема:', error);
    throw error;
  }
}

/**
 * Видалення дочірніх записів для заданого compositeId.
 */
async function deleteChildrenTreeByIdAndGroup(db, compositeId) {
  const toDelete = [];
  async function gatherDescendants(parentCompositeId) {
    const children = await db.items
      .toCollection()
      .filter((item) => item.parentId === parentCompositeId)
      .toArray();
    for (const child of children) {
      toDelete.push(child.compositeId);
      await gatherDescendants(child.compositeId);
    }
  }
  await gatherDescendants(compositeId);
  const count = await db.items
    .toCollection()
    .filter((item) => toDelete.includes(item.compositeId))
    .delete();
  // console.logStyled(`Видалено ${count} дочірніх записів для кореневого compositeId ${compositeId}`);
  return count;
}

/**
 * Видалення записів за group.
 */
async function deleteTreeByGroup(db, group) {
  try {
    const count = await db.items.where('group').equals(group).delete();
    // console.logStyled(`Видалено ${count} записів з групи: ${group}`);
    return count;
  } catch (error) {
    console.error(`Помилка при видаленні записів з групи ${group}:`, error);
    throw error;
  }
}

/**
 * Отримання всіх предків для заданого compositeId.
 */
async function getAncestorsByCompositeId(db, compositeId) {
  const ancestors = [];
  async function gather(currentCompositeId) {
    const record = await db.items
      .toCollection()
      .filter((item) => item.compositeId === currentCompositeId)
      .first();
    if (record && record.parentId) {
      const parentRecord = await db.items
        .toCollection()
        .filter((item) => item.compositeId === record.parentId)
        .first();
      if (parentRecord) {
        ancestors.push(parentRecord);
        await gather(parentRecord.compositeId);
      }
    }
  }
  await gather(compositeId);
  return ancestors;
}

export {
  createDBForPage,
  flattenItem,
  addFlattenedItemsToGroup,
  resetItemsTable,
  getHierarchicalFlatListSorted,
  getHierarchicalFlatListSortedGlobal,
  deleteDatabase,
  updateItemByIdAndGroup,
  updateItemByGroup,
  deleteChildrenTreeByIdAndGroup,
  deleteTreeByGroup,
  getAncestorsByCompositeId,
  sortGroupInDB,
};
